/**
 * Client-side mirror of the API's capability list (api/src/lib/permissions.ts).
 *
 * This decides what is RENDERED, never what is allowed. The permissions arrive
 * from the server — on the token and again from /api/me — so this file holds
 * the names and the helpers, not the role→permission map. Keeping the map on
 * one side only is deliberate: two copies drift, and a drifted copy shows a
 * Save button that then 403s.
 */

export type Permission =
  | 'VIEW_ALL' | 'VIEW_OVERSIGHT'
  | 'VIEW_CRM_QUEUE' | 'VIEW_PURCHASE_QUEUE' | 'VIEW_DISPATCH_QUEUE' | 'VIEW_ALL_CRMS'
  | 'CREATE_PO' | 'EDIT_PO'
  | 'CREATE_PRODUCT' | 'EDIT_PRODUCT'
  | 'CREATE_JOB' | 'EDIT_JOB'
  | 'EDIT_MASTERS' | 'EDIT_VENDORS' | 'EDIT_CLIENTS' | 'EDIT_CONTRACTORS'
  | 'MANAGE_INVENTORY' | 'VIEW_INVENTORY' | 'VIEW_BOARDS' | 'ISSUE_INVENTORY' | 'VERIFY_STORE_ISSUE'
  | 'VIEW_PLANNER'
  | 'VIEW_CUTTING_QUEUE' | 'RECORD_CUTTING'
  | 'VIEW_PRINTING_QUEUE' | 'RECORD_PRINTING'
  | 'MANAGE_BARCODES'
  | 'SET_PROMISE_DATE' | 'CANCEL_ORDER'
  | 'MARK_DESIGN_DONE' | 'MARK_BOARD_STATUS' | 'RECORD_DISPATCH' | 'SET_ORDER_TYPE'
  | 'VIEW_QC_QUEUE' | 'QC_INSPECT' | 'QC_APPROVE' | 'VIEW_MASTER_FILES'
  | 'MANAGE_BILLING' | 'VIEW_BILLING' | 'UNLOCK_BILLING' | 'CORRECT_LOCKED_RECORDS'
  | 'ADD_PRODUCTION_ENTRY' | 'VERIFY_PRODUCTION'
  | 'EXPORT_REPORTS'
  | 'MANAGE_USERS' | 'ASSIGN_CRM' | 'DELETE_RECORDS' | 'RUN_BACKFILLS'
  | 'APPROVE_JOB_EDIT';

/** Role ids, matching tblRole and the API's ROLE constant. */
export const ROLE = {
  Admin: 1,
  CRM: 2,
  Designers: 3,
  Purchase: 4,
  Dispatch: 5,
  VPPL: 6,
  /** Process Coordinator — read-only oversight across every CRM. */
  PC: 7,
  /** Super Admin — every permission. */
  SuperAdmin: 8,
  Planner: 9,
  Accounts: 10,
  /** GM — view everything, approve VPPL edit requests, edit nothing else. */
  GM: 11,
  /** Quality / QC inspector — owns the inspection step between production
   *  and dispatch. See the API's permissions.ts for the full write surface. */
  Quality: 12,
  /** Cutting Operator */
  Cutting: 13,
  /** Store keeper — issues raw material; Purchase / Super Admin verify each issue. */
  Store: 14,
  /** Printing operator — the plan by machine; records what was printed. */
  Printing: 15,
} as const;

export type RoleId = (typeof ROLE)[keyof typeof ROLE];
